import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getCompany, getProducts, sendMessage, saveSupplier, fetchWithCredentials } from '@/lib/api';
import { getFirstImage } from '@/utils/imageUtils';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import CompanyRatings from '@/components/ratings/CompanyRatings';
import { 
  ArrowLeft, 
  Building2, 
  MapPin, 
  Phone,
  Mail,
  Globe,
  Package,
  MessageSquare,
  FileText,
  Bookmark,
  CheckCircle2,
  ImageIcon,
  Loader2,
  Award,
  Star,
  CalendarCheck,
  Crown,
  Sparkles,
  Lock,
  KeyRound,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyProfile = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { isUSA } = useLocale();
  
  const [company, setCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [serviceQuoteDialogOpen, setServiceQuoteDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  const [catalogAccessStatus, setCatalogAccessStatus] = useState(null); // null, 'pending', 'approved'
  const [contactAccessStatus, setContactAccessStatus] = useState(null); // null, 'pending', 'approved'
  const [messageForm, setMessageForm] = useState({
    subject: '',
    content: ''
  });
  const [serviceQuoteForm, setServiceQuoteForm] = useState({
    service_type: '',
    description: '',
    images: []
  });

  // Check if viewing own company - must recalculate when user changes
  const isOwnCompany = React.useMemo(() => {
    if (!user?.company_id || !companyId) return false;
    return String(user.company_id) === String(companyId);
  }, [user?.company_id, companyId]);

  // Check catalog and contact access
  const checkAccess = async () => {
    if (!isAuthenticated) return;
    
    try {
      // Check catalog access
      const catalogRes = await fetch(`${API_URL}/api/catalog-access/${companyId}/check`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}` },
        credentials: 'include'
      });
      if (catalogRes.ok) {
        const data = await catalogRes.json();
        setCatalogAccessStatus(data.has_access ? 'approved' : (data.pending_request ? 'pending' : null));
      }
      
      // Check contact access
      const contactRes = await fetch(`${API_URL}/api/contact-access/${companyId}/check`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}` },
        credentials: 'include'
      });
      if (contactRes.ok) {
        const data = await contactRes.json();
        setContactAccessStatus(data.has_access ? 'approved' : (data.pending_request ? 'pending' : null));
      }
    } catch (error) {
      console.error('Error checking access:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyData, productsData] = await Promise.all([
          getCompany(companyId),
          getProducts({ company_id: companyId })
        ]);
        setCompany(companyData);
        setProducts(productsData);
      } catch (error) {
        toast.error(isUSA ? 'Company not found' : 'Empresa no encontrada');
        navigate('/search');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    checkAccess();
  }, [companyId, navigate, isAuthenticated]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error(isUSA ? 'You must log in' : 'Debes iniciar sesión');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await sendMessage({
        recipient_company_id: companyId,
        ...messageForm
      });
      toast.success(isUSA ? 'Message sent' : 'Mensaje enviado');
      setMessageDialogOpen(false);
      setMessageForm({ subject: '', content: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSupplier = async () => {
    if (!isAuthenticated) {
      toast.error(isUSA ? 'You must log in' : 'Debes iniciar sesión');
      navigate('/login');
      return;
    }

    try {
      await saveSupplier(companyId);
      toast.success(isUSA ? 'Supplier saved' : 'Proveedor guardado');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRequestContactAccess = async () => {
    if (!isAuthenticated) {
      toast.error(isUSA ? 'You must log in to request access' : 'Debes iniciar sesión para solicitar acceso');
      navigate('/login');
      return;
    }

    // Use XMLHttpRequest to avoid body stream issues with interceptors
    const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/companies/${companyId}/request-contact-access`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      if (xhr.status === 200 || xhr.status === 201) {
        toast.success(isUSA ? 'Request sent. The supplier will review your request.' : 'Solicitud enviada. El proveedor revisará tu solicitud.');
        setContactAccessStatus('pending');
      } else {
        let errorMessage = isUSA ? 'Error sending request' : 'Error al enviar solicitud';
        try {
          const errorData = JSON.parse(xhr.responseText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Use default message
        }
        toast.error(errorMessage);
      }
    };
    
    xhr.onerror = function() {
      toast.error(isUSA ? 'Error sending request' : 'Error al enviar solicitud');
    };
    
    xhr.send();
  };

  const handleRequestCatalogAccess = async () => {
    if (!isAuthenticated) {
      toast.error(isUSA ? 'You must log in to request access' : 'Debes iniciar sesión para solicitar acceso');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    
    // Use XMLHttpRequest to avoid body stream issues with interceptors
    const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/companies/${companyId}/request-catalog-access`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      setSubmitting(false);
      if (xhr.status === 200 || xhr.status === 201) {
        toast.success(isUSA ? 'Request sent. The supplier will review your request.' : 'Solicitud enviada. El proveedor revisará tu solicitud.');
        setCatalogAccessStatus('pending');
      } else {
        let errorMessage = isUSA ? 'Error sending request' : 'Error al enviar solicitud';
        try {
          const errorData = JSON.parse(xhr.responseText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Use default message
        }
        toast.error(errorMessage);
      }
    };
    
    xhr.onerror = function() {
      setSubmitting(false);
      toast.error(isUSA ? 'Error sending request' : 'Error al enviar solicitud');
    };
    
    xhr.send();
  };

  const handleServiceQuote = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error(isUSA ? 'You must log in' : 'Debes iniciar sesión');
      navigate('/login');
      return;
    }

    if (!serviceQuoteForm.service_type || !serviceQuoteForm.description) {
      toast.error(isUSA ? 'Please complete the required fields' : 'Por favor completa los campos requeridos');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/service-quotes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          target_company_id: companyId,
          ...serviceQuoteForm
        })
      });

      if (res.ok) {
        toast.success(isUSA ? 'Quote request sent' : 'Solicitud de cotización enviada');
        setServiceQuoteDialogOpen(false);
        setServiceQuoteForm({ service_type: '', description: '', images: [] });
      } else {
        const data = await res.json();
        toast.error(data.detail || (isUSA ? 'Error sending' : 'Error al enviar'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error sending request' : 'Error al enviar solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Cover Image with Back Button */}
      <div 
        className="h-48 md:h-64 bg-slate-900 bg-cover bg-center relative"
        style={{ backgroundImage: company.cover_url ? `url(${company.cover_url})` : undefined }}
      >
        {/* Back Button - positioned inside cover */}
        <div className="absolute top-4 left-4 z-20">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="bg-white/90 hover:bg-white shadow-md"
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            {isUSA ? 'Back' : 'Volver'}
          </Button>
        </div>
      </div>

      <div className="container-app -mt-16 relative z-10">
        {/* Company Header */}
        <Card className="card-industrial mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              {/* Logo */}
              <div className="-mt-20 md:-mt-16">
                {company.logo_url ? (
                  <img 
                    src={company.logo_url} 
                    alt={company.company_name}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-xl border-4 border-white shadow-lg object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl border-4 border-white shadow-lg bg-slate-100 flex items-center justify-center">
                    <Building2 className="w-12 h-12 text-slate-400" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
                        {company.company_name || company.name}
                      </h1>
                      {company.verified && (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      )}
                      {/* Plan de suscripción */}
                      {company.subscription_plan && company.subscription_plan !== 'free' && (
                        <Badge className={
                          company.subscription_plan === 'sponsor' 
                            ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0"
                            : "bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0"
                        }>
                          {company.subscription_plan === 'sponsor' ? (
                            <>
                              <Sparkles className="w-3 h-3 mr-1" />
                              {isUSA ? 'Sponsor' : 'Patrocinador'}
                            </>
                          ) : (
                            <>
                              <Crown className="w-3 h-3 mr-1" />
                              PRO
                            </>
                          )}
                        </Badge>
                      )}
                      {/* Badge Destacado por referidos */}
                      {company.is_featured && (
                        <Badge className="bg-gradient-to-r from-emerald-400 to-green-500 text-white border-0">
                          <Star className="w-3 h-3 mr-1 fill-white" />
                          {isUSA ? 'Featured' : 'Destacado'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-slate-600 mt-1">
                      {company.company_type === 'proveedor' 
                        ? (isUSA ? 'Supplier' : 'Proveedor') 
                        : company.company_type === 'fabricante' 
                          ? (isUSA ? 'Manufacturer' : 'Fabricante') 
                          : (isUSA ? 'Supplier and Manufacturer' : 'Proveedor y Fabricante')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={handleSaveSupplier}>
                      <Bookmark className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-600">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {company.location_city}, {company.location_state}
                  </div>
                  {/* Show contact data based on user plan and hide_contact_data setting */}
                  {(() => {
                    const userPlan = user?.subscription_plan || 'free';
                    const canViewContacts = userPlan === 'pro';
                    const hasContactAccess = contactAccessStatus === 'approved';
                    
                    // If contact data is hidden and user doesn't have special access
                    if (company.hide_contact_data && !isOwnCompany && !hasContactAccess) {
                      return (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                          <Lock className="w-4 h-4" />
                          <span className="text-sm">{isUSA ? 'Protected contact data' : 'Datos de contacto protegidos'}</span>
                        </div>
                      );
                    }
                    
                    // If user has access (approved, own company, or contact is public)
                    if (isOwnCompany || hasContactAccess || !company.hide_contact_data) {
                      // Check if free user trying to view public contact data
                      if (!canViewContacts && !isOwnCompany && !hasContactAccess && isAuthenticated) {
                        return null; // Will show upgrade button below
                      }
                      
                      return (
                        <>
                          {company.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {company.phone}
                            </div>
                          )}
                          {company.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {company.email}
                            </div>
                          )}
                          {company.website && (
                            <a 
                              href={company.website} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
                            >
                              <Globe className="w-4 h-4" />
                              {isUSA ? 'Website' : 'Sitio web'}
                            </a>
                          )}
                        </>
                      );
                    }
                    
                    return null;
                  })()}
                </div>

                {/* Upgrade prompt for free users - only show on OTHER company profiles */}
                {isAuthenticated && !isOwnCompany && (user?.subscription_plan || 'free') === 'free' && !company.hide_contact_data && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Lock className="w-5 h-5 text-emerald-600" />
                        <div>
                          <p className="font-medium text-slate-900">{isUSA ? 'Contact data' : 'Datos de contacto'}</p>
                          <p className="text-sm text-slate-600">{isUSA ? 'Upgrade your plan to see phone, email and website' : 'Actualiza tu plan para ver teléfono, email y sitio web'}</p>
                        </div>
                      </div>
                      <Button 
                        onClick={() => navigate('/dashboard/subscription')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        {isUSA ? 'View contact data' : 'Ver datos de contacto'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Request Contact Access Button - only show when contact data is hidden by owner, not own company, and no access yet */}
                {company.hide_contact_data && isAuthenticated && !isOwnCompany && contactAccessStatus !== 'approved' && (
                  <div className="mt-4">
                    {contactAccessStatus === 'pending' ? (
                      <Badge className="bg-amber-100 text-amber-700 px-4 py-2">
                        {isUSA ? 'Access request pending' : 'Solicitud pendiente'}
                      </Badge>
                    ) : (
                      <Button 
                        variant="outline" 
                        onClick={handleRequestContactAccess}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      >
                        <KeyRound className="w-4 h-4 mr-2" />
                        {isUSA ? 'Request contact data' : 'Solicitar datos de contacto'}
                      </Button>
                    )}
                  </div>
                )}

                {/* Scheduling Badge */}
                {company.accepts_scheduling && (
                  <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <CalendarCheck className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">{isUSA ? 'Accepts scheduling requests' : 'Acepta solicitudes de programación'}</span>
                  </div>
                )}

                {/* Certifications */}
                {company.certifications && company.certifications.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {company.certifications.map((cert, index) => (
                      <Badge key={index} variant="outline" className="flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        {cert}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-slate-200">
              <Button 
                variant="outline"
                onClick={() => setActiveTab('products')}
              >
                <Package className="mr-2 w-4 h-4" />
                {isUSA ? `View catalog (${products.length})` : `Ver catálogo (${products.length})`}
              </Button>

              {company.accepts_scheduling && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
                      <CalendarCheck className="mr-2 w-4 h-4" />
                      {isUSA ? 'Request scheduling' : 'Solicitar programación'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>{isUSA ? `Request scheduling from ${company.company_name}` : `Solicitar programación a ${company.company_name}`}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      try {
                        const res = await fetch(`${API_URL}/api/scheduling/request`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            supplier_id: companyId,
                            message: formData.get('message')
                          })
                        });
                        if (res.ok) {
                          toast.success(isUSA ? 'Request sent successfully' : 'Solicitud enviada correctamente');
                          e.target.reset();
                        } else {
                          const data = await res.json();
                          toast.error(data.detail || (isUSA ? 'Error sending' : 'Error al enviar'));
                        }
                      } catch (error) {
                        toast.error(isUSA ? 'Error sending request' : 'Error al enviar solicitud');
                      }
                    }} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{isUSA ? 'Message (optional)' : 'Mensaje (opcional)'}</Label>
                        <Textarea
                          name="message"
                          placeholder={isUSA ? 'Describe your scheduling needs: products, quantities, dates...' : 'Describe tus necesidades de programación: productos, cantidades, fechas...'}
                          rows={4}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="submit" style={{ backgroundColor: '#34B772' }}>
                          {isUSA ? 'Send request' : 'Enviar solicitud'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}

              {/* WhatsApp Button - show if has whatsapp number, or use phone as fallback */}
              {(() => {
                // Determine if we should show WhatsApp button
                const hasContactAccess = contactAccessStatus === 'approved';
                const userPlan = user?.subscription_plan || 'free';
                const canViewContacts = userPlan === 'pro' || isOwnCompany || hasContactAccess;
                
                // If contact is hidden and user doesn't have access, don't show WhatsApp
                if (company.hide_contact_data && !isOwnCompany && !hasContactAccess) {
                  return null;
                }
                
                // Get the WhatsApp number (prefer configured, fallback to phone)
                const whatsappNum = company.whatsapp_number || (company.phone ? company.phone.replace(/[\s\-\(\)]/g, '') : null);
                
                // Only show if we have a valid number
                if (!whatsappNum) return null;
                
                // For free users viewing public contact, only show if they have PRO or special access
                if (!canViewContacts && !isAuthenticated) {
                  return null;
                }
                
                return (
                  <Button 
                    variant="outline" 
                    className="border-green-300 text-green-700 hover:bg-green-50"
                    onClick={() => window.open(`https://wa.me/${whatsappNum}?text=${encodeURIComponent(isUSA ? `Hello, I'm contacting you from TexHub. I'm interested in ${company.company_name}.` : `Hola, me comunico desde TexHub. Me interesa contactar a ${company.company_name}.`)}`, '_blank')}
                    data-testid="whatsapp-button"
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </Button>
                );
              })()}

              <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <MessageSquare className="mr-2 w-4 h-4" />
                    {isUSA ? 'Send message' : 'Enviar mensaje'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>{isUSA ? `Send message to ${company.company_name}` : `Enviar mensaje a ${company.company_name}`}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSendMessage} className="space-y-4">
                    <div className="space-y-2">
                      <Label>{isUSA ? 'Subject *' : 'Asunto *'}</Label>
                      <Input
                        value={messageForm.subject}
                        onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                        placeholder={isUSA ? 'Message subject' : 'Asunto del mensaje'}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{isUSA ? 'Message *' : 'Mensaje *'}</Label>
                      <Textarea
                        value={messageForm.content}
                        onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                        placeholder={isUSA ? 'Write your message...' : 'Escribe tu mensaje...'}
                        rows={5}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Send message' : 'Enviar mensaje')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Service Quote Button - only for fabricantes/maquileros with offered_services */}
              {company.offered_services && company.offered_services.length > 0 && (
                <Dialog open={serviceQuoteDialogOpen} onOpenChange={setServiceQuoteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700">
                      <FileText className="mr-2 w-4 h-4" />
                      {isUSA ? 'Quote Service' : 'Cotizar Servicio'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{isUSA ? 'Request service quote' : 'Solicitar cotización de servicio'}</DialogTitle>
                      <p className="text-sm text-slate-500">
                        {isUSA ? 'Select the service you need and describe your project' : 'Selecciona el servicio que necesitas y describe tu proyecto'}
                      </p>
                    </DialogHeader>
                    <form onSubmit={handleServiceQuote} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{isUSA ? 'Service type *' : 'Tipo de servicio *'}</Label>
                        <Select
                          value={serviceQuoteForm.service_type}
                          onValueChange={(value) => setServiceQuoteForm({ ...serviceQuoteForm, service_type: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={isUSA ? 'Select a service' : 'Selecciona un servicio'} />
                          </SelectTrigger>
                          <SelectContent>
                            {company.offered_services.map((service, idx) => (
                              <SelectItem key={idx} value={service}>{service}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{isUSA ? 'Describe what you need *' : 'Describe lo que necesitas *'}</Label>
                        <Textarea
                          value={serviceQuoteForm.description}
                          onChange={(e) => setServiceQuoteForm({ ...serviceQuoteForm, description: e.target.value })}
                          placeholder={isUSA ? 'Describe your product or service: materials, quantities, specifications, delivery times...' : 'Describe tu producto o servicio: materiales, cantidades, especificaciones, tiempos de entrega...'}
                          rows={4}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{isUSA ? 'Reference photos (URLs)' : 'Fotos de referencia (URLs)'}</Label>
                        <Input
                          placeholder={isUSA ? 'Paste image URLs separated by comma' : 'Pega URLs de imágenes separadas por coma'}
                          onChange={(e) => {
                            const urls = e.target.value.split(',').map(url => url.trim()).filter(url => url);
                            setServiceQuoteForm({ ...serviceQuoteForm, images: urls });
                          }}
                        />
                        <p className="text-xs text-slate-500">
                          {isUSA ? 'You can upload your photos to a service like Imgur and paste the links here' : 'Puedes subir tus fotos a un servicio como Imgur y pegar los links aquí'}
                        </p>
                      </div>
                      <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={submitting || !serviceQuoteForm.service_type}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Send quote request' : 'Enviar solicitud de cotización')}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList>
            <TabsTrigger value="about">{isUSA ? 'About' : 'Acerca de'}</TabsTrigger>
            <TabsTrigger value="products">{isUSA ? `Catalog (${products.length})` : `Catálogo (${products.length})`}</TabsTrigger>
            <TabsTrigger value="ratings" className="flex items-center gap-1">
              <Star className="w-4 h-4" />
              {isUSA ? 'References' : 'Referencias'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="about" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Description */}
              <Card className="card-industrial">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">{isUSA ? 'Description' : 'Descripción'}</h3>
                  <p className="text-slate-600">
                    {isUSA && company.description_en 
                      ? company.description_en 
                      : company.description || (isUSA ? 'This company has not added a description.' : 'Esta empresa no ha agregado una descripción.')}
                  </p>
                </CardContent>
              </Card>

              {/* Capacities */}
              <Card className="card-industrial">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">{isUSA ? 'Capabilities' : 'Capacidades'}</h3>
                  <div className="space-y-3">
                    {company.production_capacity && (
                      <div>
                        <span className="text-slate-500 text-sm">{isUSA ? 'Production capacity' : 'Capacidad de producción'}</span>
                        <p className="font-medium">{company.production_capacity}</p>
                      </div>
                    )}
                    {company.avg_delivery_time && (
                      <div>
                        <span className="text-slate-500 text-sm">{isUSA ? 'Average delivery time' : 'Tiempo de entrega promedio'}</span>
                        <p className="font-medium">{company.avg_delivery_time}</p>
                      </div>
                    )}
                    {company.industry && (
                      <div>
                        <span className="text-slate-500 text-sm">{isUSA ? 'Industry' : 'Giro'}</span>
                        <p className="font-medium">{company.industry}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            {/* Check if catalog is private and user doesn't have access */}
            {company?.catalog_private && catalogAccessStatus !== 'approved' && !isOwnCompany ? (
              <div className="text-center py-12 bg-slate-50 rounded-xl">
                <Lock className="w-16 h-16 mx-auto text-slate-400 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {isUSA ? 'Private Catalog' : 'Catálogo Privado'}
                </h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  {isUSA 
                    ? 'This company has restricted access to their catalog. Request access to view their products.'
                    : 'Esta empresa ha restringido el acceso a su catálogo. Solicita acceso para ver sus productos.'}
                </p>
                {!isAuthenticated ? (
                  <Button onClick={() => navigate('/login')}>
                    {isUSA ? 'Log in to request access' : 'Inicia sesión para solicitar acceso'}
                  </Button>
                ) : catalogAccessStatus === 'pending' ? (
                  <Badge className="bg-amber-100 text-amber-700 px-4 py-2">
                    <Clock className="w-4 h-4 mr-2 inline" />
                    {isUSA ? 'Access request pending' : 'Solicitud de acceso pendiente'}
                  </Badge>
                ) : isOwnCompany ? (
                  <Badge className="bg-green-100 text-green-700 px-4 py-2">
                    {isUSA ? 'This is your company' : 'Esta es tu empresa'}
                  </Badge>
                ) : (
                  <Button onClick={handleRequestCatalogAccess} disabled={submitting}>
                    {submitting ? (isUSA ? 'Sending...' : 'Enviando...') : (isUSA ? 'Request catalog access' : 'Solicitar acceso al catálogo')}
                  </Button>
                )}
              </div>
            ) : products.length === 0 ? (
              <div className="empty-state">
                <Package className="empty-state-icon" />
                <h3 className="empty-state-title">{isUSA ? 'No products' : 'Sin productos'}</h3>
                <p className="empty-state-text">{isUSA ? 'This company has not published products yet' : 'Esta empresa no ha publicado productos aún'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <Card 
                    key={product.product_id} 
                    className="product-card cursor-pointer"
                    onClick={() => navigate(`/products/${product.product_id}`)}
                  >
                    {product.images && product.images.length > 0 ? (
                      <img 
                        src={getFirstImage(product.images)} 
                        alt={product.name}
                        className="product-card-image"
                      />
                    ) : (
                      <div className="product-card-image flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    <CardContent className="product-card-content">
                      <h3 className="font-semibold text-slate-900 line-clamp-1">{isUSA && product.name_en ? product.name_en : product.name}</h3>
                      <p className="text-sm text-slate-500">{isUSA && product.category === 'telas' ? 'Fabrics' : product.category}</p>
                      <div className="mt-2">
                        {!product.hide_price && product.reference_price ? (
                          <span className="font-semibold text-slate-900">
                            ${product.reference_price.toLocaleString()} {product.currency || 'MXN'}/{product.unit || (product.category === 'telas' ? 'Mt' : (isUSA ? 'Unit' : 'pza'))}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-500">{isUSA ? 'Price on request' : 'Precio bajo cotización'}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="ratings" className="mt-6">
            <CompanyRatings 
              companyId={companyId} 
              companyName={company.company_name}
              companyType={company.company_type}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CompanyProfile;
